// ============================================================================
// BOQ — Sub-trade catalogue hook (the standardized "pick a sub-trade" list)
// ============================================================================
// Reads boq.trade_template_lines for the SD stage: the canonical, QS-locked
// list of sub-trades (one row per sub-trade, carrying its parent trade, a
// default unit, and the canonical sort order). These are the same rows that
// seed a fresh project, so the "Add allowance line" picker stays in lock-step
// with the template — including any custom sub-trades added via
// create_custom_subtrade (they get an SD template line too).
//
// SD is the single stored template (TD/DD/GFC reuse it via an SD fallback in
// create_project_from_template), so we read the SD rows and use them for the
// picker regardless of the project's stage.
//
// RLS: trade_template_lines is read-for-authenticated. Read-only hook.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const SELECT =
  'subtrade_node_id, trade_node_id, trade_label, subtrade_label, ' +
  'default_unit, default_line_type, is_core, is_custom, sort_order';

const mapEntry = (r) => ({
  subtradeNodeId: r.subtrade_node_id,
  tradeNodeId: r.trade_node_id,
  tradeLabel: r.trade_label,
  subtradeLabel: r.subtrade_label,
  defaultUnit: r.default_unit || '',
  defaultLineType: r.default_line_type || 'allowance',
  isCore: r.is_core !== false,
  isCustom: r.is_custom === true,
  sortOrder: r.sort_order ?? 0,
});

export const useBoqSubtradeCatalogue = ({ enabled = true } = {}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Resolve the SD spec_depth node (code 'sd' under the spec_depth
      // taxonomy) — the template key. Mirrors create_custom_subtrade's lookup.
      const { data: sdNodes, error: sdErr } = await supabase
        .schema('boq')
        .from('classification_nodes')
        .select('id, classification_taxonomies!inner(code)')
        .eq('code', 'sd')
        .eq('classification_taxonomies.code', 'spec_depth');
      if (sdErr) throw sdErr;
      const sdNodeId = sdNodes?.[0]?.id;
      if (!sdNodeId) throw new Error('SD stage node not found in taxonomy.');

      const { data, error: fErr } = await supabase
        .schema('boq')
        .from('trade_template_lines')
        .select(SELECT)
        .eq('spec_depth_node_id', sdNodeId)
        .order('sort_order');
      if (fErr) throw fErr;
      setEntries((data || []).map(mapEntry));
    } catch (err) {
      console.error('[useBoqSubtradeCatalogue] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'sub-trade catalogue' }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  return { entries, loading, error, refresh: fetchAll };
};

export default useBoqSubtradeCatalogue;
